// Hero.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConsultationModal from './ConsultationModal';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showConsultationModal, setShowConsultationModal] = useState(false);

  const slides = [
    {
      tag: 'Global mobility',
      heading: 'Study and Work Abroad\nWith Structured Support',
      description: 'We help individuals navigate international study and employment pathways with clarity and professional guidance. From consultation to applications, we provide the support you need to move forward with confidence.',
      primaryCTA: { text: 'Book a Consultation', action: 'consultation' },
      secondaryCTA: { text: 'Speak to an Advisor', action: 'consultation' },
      image: 'hero-1.jpg',
      serviceType: 'study-abroad' // Default to study-abroad for general consultation
    },
    {
      tag: 'Study Abroad',
      heading: 'University Admissions\nFrom Shortlist to Offer',
      description: 'We support international students through the entire admissions journey. From selecting universities to preparing statements and submitting applications, our structured approach keeps you informed and organized.',
      primaryCTA: { text: 'Explore Study Abroad', link: '/study-abroad' },
      secondaryCTA: { text: 'Book Study Consultation', action: 'consultation' },
      image: 'hero-2.jpg',
      serviceType: 'study-abroad'
    },
    {
      tag: 'Work Abroad',
      heading: 'Verified Job Opportunities\nWith Interview Support',
      description: 'Access verified overseas employers and guaranteed interview opportunities for eligible applicants. From consultation to interview readiness, we guide you through each stage with transparency and care.',
      primaryCTA: { text: 'Explore Work Abroad', link: '/work-abroad' },
      secondaryCTA: { text: 'Book Work Consultation', action: 'consultation' },
      image: 'hero-3.jpg',
      serviceType: 'work-abroad'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 7000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative w-full h-[600px] md:h-[700px] overflow-hidden bg-white">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          <div className="max-w-7xl mx-auto h-full grid md:grid-cols-2">
            {/* Left Content */}
            <div className="flex flex-col justify-center px-4 md:px-8 py-12 bg-[#F5F8FA]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="inline-block bg-white text-[#2542D4] text-xs font-light px-4 py-2 rounded-md mb-4">
                  {slides[currentSlide].tag}
                </span>
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#202356] leading-tight mb-6 whitespace-pre-line">
                  {slides[currentSlide].heading}
                </h1>
                
                <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl font-light">
                  {slides[currentSlide].description}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  {slides[currentSlide].primaryCTA.link ? (
                    <Link
                      to={slides[currentSlide].primaryCTA.link}
                      className="bg-[#202356] hover:bg-[#2d3270] text-white font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].primaryCTA.text}
                    </Link>
                  ) : (
                    <button
                      onClick={() => setShowConsultationModal(true)}
                      className="bg-[#202356] hover:bg-[#2d3270] text-white font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].primaryCTA.text}
                    </button>
                  )}
                  {slides[currentSlide].secondaryCTA.link ? (
                    <Link
                      to={slides[currentSlide].secondaryCTA.link}
                      className="border border-gray-300 hover:border-[#202356] hover:text-[#202356] text-gray-700 font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].secondaryCTA.text}
                    </Link>
                  ) : (
                    <button
                      onClick={() => setShowConsultationModal(true)}
                      className="border border-gray-300 hover:border-[#202356] hover:text-[#202356] text-gray-700 font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].secondaryCTA.text}
                    </button>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Right Image */}
            <div className="relative hidden md:block">
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${require(`../assets/${slides[currentSlide].image}`)})`,
                }}
              />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSlide ? 'bg-[#202356] w-8' : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Consultation Modal */}
      <ConsultationModal 
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        serviceType={slides[currentSlide].serviceType}
      />
    </section>
  );
};

export default Hero;
